'use client';

import React from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface GalleryImage {
  id: string;
  src: string;
  alt: string;
  title: string;
  description: string;
  category: string;
  isFoodItem: boolean;
  menuLink?: string;
}

interface GalleryGridProps {
  images: GalleryImage[];
  onImageClick: (image: GalleryImage) => void;
  onFavorite: (imageId: string) => void;
  favorites: Set<string>;
}

const GalleryGrid = ({ images, onImageClick, onFavorite, favorites }: GalleryGridProps) => {
  return (
    <div className="grid grid-cols-2 gap-4 px-4 pb-8 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
      {images.map((image) => (
        <div
          key={image.id}
          className="group relative overflow-hidden rounded-lg bg-card shadow-md transition-smooth hover-lift hover:shadow-xl"
        >
          <div className="relative aspect-square overflow-hidden">
            <AppImage
              src={image.src}
              alt={image.alt}
              className="h-full w-full cursor-pointer object-cover transition-smooth group-hover:scale-110"
              onClick={() => onImageClick(image)}
            />

            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/90 via-foreground/40 to-transparent opacity-0 transition-smooth group-hover:opacity-100">
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <h3 className="text-sm font-semibold text-primary-foreground line-clamp-1">
                  {image.title}
                </h3>
                <p className="mt-1 text-xs text-primary-foreground/80 line-clamp-2">
                  {image.description}
                </p>
              </div>
            </div>

            {/* Favorite Button */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onFavorite(image.id);
              }}
              className="absolute right-2 top-2 rounded-full bg-background/80 p-2 transition-smooth hover:bg-background focus-ring"
              aria-label={favorites.has(image.id) ? 'Remove from favorites' : 'Add to favorites'}
            >
              <Icon
                name="HeartIcon"
                size={18}
                variant={favorites.has(image.id) ? 'solid' : 'outline'}
                className={favorites.has(image.id) ? 'text-error' : 'text-foreground'}
              />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default GalleryGrid;
